import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { unlink } from 'firebase/auth';

import { linkPendingCredential } from '@mopla/business-logic';

import { MplLoader } from '../../atoms/MplLoader/MplLoader';
import { getSocialLoginErrorMessage } from '../SocialLogin/getSocialLoginErrorMessage';
import { LoginWithApple } from '../SocialLogin/LoginWithApple';
import { LoginWithFacebook } from '../SocialLogin/LoginWithFacebook';
import { LoginWithGoogle } from '../SocialLogin/LoginWithGoogle';
import { SocialLoginError } from '../SocialLogin/SocialLoginError';
import { SocialLoginErrorModal } from '../SocialLogin/SocialLoginErrorModal';
import {
	ISocialLoginResult,
	SocialLoginErrorCodeEnum,
	TSocialLoginMethod,
} from '../SocialLogin/types';

type DriverSocialLoginProps = {
	validateUser: () => Promise<boolean>;
	socialLoginMethods: TSocialLoginMethod[];
};

export const DriverSocialLogin: FC<DriverSocialLoginProps> = ({
	validateUser,
	socialLoginMethods,
}) => {
	const { t } = useTranslation('socialLogin');
	const [isLoading, setIsLoading] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [error, setError] = useState<SocialLoginError | null>(null);
	const navigate = useNavigate();

	const handleError = (err: SocialLoginError) => {
		setError(err);
		setShowErrorModal(true);
	};

	const socialSignIn = async (signInFn: () => Promise<ISocialLoginResult>) => {
		try {
			setIsLoading(true);
			const { user, providerId } = await signInFn();

			if (!user || !providerId) {
				throw new SocialLoginError(SocialLoginErrorCodeEnum.USER_NOT_FOUND);
			}
			const valid = await validateUser();
			if (!valid) {
				await unlink(user, providerId);
				throw new SocialLoginError(SocialLoginErrorCodeEnum.USER_NOT_FOUND);
			}

			await linkPendingCredential();
			setIsLoading(false);
			navigate('/home');
		} catch (err) {
			if (err instanceof SocialLoginError) {
				const { title, description } = getSocialLoginErrorMessage(err, t);
				setIsLoading(false);
				handleError(
					new SocialLoginError(SocialLoginErrorCodeEnum.USER_NOT_FOUND, {
						title,
						description,
					})
				);
			}
			console.error(err);
		} finally {
			setIsLoading(false);
		}
	};
	return (
		<>
			<Stack direction="column" gap={2}>
				{socialLoginMethods.includes('google') && (
					<LoginWithGoogle onSignIn={socialSignIn} />
				)}
				{socialLoginMethods.includes('apple') && (
					<LoginWithApple onSignIn={socialSignIn} />
				)}
				{socialLoginMethods.includes('facebook') && (
					<LoginWithFacebook onSignIn={socialSignIn} />
				)}
			</Stack>
			<SocialLoginErrorModal
				isOpen={showErrorModal}
				onClose={() => setShowErrorModal(false)}
				errorTitle={error?.title}
				errorDescription={error?.description}
			/>
			<MplLoader isLoading={isLoading} />
		</>
	);
};
